import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierActivationRequests } from './fetchSupplierActivationRequests';
import { type SupplierActivationRequestsResponse } from './types';

/**
 * Generates the query options for fetching all activation requests for a data-exchange.
 *
 * @param deId - The data-exchange ID.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export
export const getSupplierActivationRequestsQueryOptions = <
  TData = SupplierActivationRequestsResponse,
>({
  deId,
  options,
}: {
  deId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<
      SupplierActivationRequestsResponse,
      VestigasHTTPError,
      TData
    >,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: deId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchSupplierActivationRequests(deId, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getActivationRequests(deId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for fetching all activation requests for a data-exchange,
 * ordered newest first.
 *
 * @param deId - The data-exchange ID.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_activation_requests_for_de_suppliers__de_id__activation_requests_get
 */
export const useQuerySupplierActivationRequests = <
  TData = SupplierActivationRequestsResponse,
>(
  deId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<
      SupplierActivationRequestsResponse,
      VestigasHTTPError,
      TData
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(getSupplierActivationRequestsQueryOptions<TData>({ deId, options }));
